import { gql } from '@apollo/client';
import {
  ItemDetail_DemoVariantPacksQuery,
  ItemDetail_DemoVariantPacksQueryVariables,
  ItemsContext_VariantsPackFieldsFragment,
} from '@/types';
import { useAdaptAndGroupVariantPacks } from './use-adapt-and-group-item-list-options';
import { useItemQueryVariables } from './use-item-query-variables';
import { useMemo } from 'react';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from './use-wrapped-query';
import { VARIANTS_PACK_FRAGMENT } from '@/contexts/items-context-queries';

const ITEM_DETAILS_DEMO_VARIANT_PACKS_QUERY = gql`
  query ItemDetail_DemoVariantPacks(
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
    $variantPackUUIDs: [uuid!]!
  ) {
    visible_variant_packs(
      where: {
        variant_pack_uuid: { _in: $variantPackUUIDs }
        restaurant_uuid: { _eq: $restaurantUUID }
        supplier_uuid: { _eq: $supplierUUID }
      }
    ) {
      variant_pack {
        ...ItemsContext_VariantsPackFields
      }
    }
  }

  ${VARIANTS_PACK_FRAGMENT}
`;

interface Options {
  recommendedVariantPackUUIDs: string[];
  relatedVariantPackUUIDs: string[];
}

export const useGetItemDetailsDemoVariantPacks = ({
  recommendedVariantPackUUIDs,
  relatedVariantPackUUIDs,
}: Options) => {
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks();
  const itemQueryVariables = useItemQueryVariables();
  const { restaurantUUID, supplierUUID } = useNewOrderContext();
  const { isGuestMode } = useUserContext();

  const variantPackUUIDs = useMemo(
    () => [
      ...new Set([...recommendedVariantPackUUIDs, ...relatedVariantPackUUIDs]),
    ],
    [recommendedVariantPackUUIDs, relatedVariantPackUUIDs],
  );

  const { data } = useWrappedQuery<
    ItemDetail_DemoVariantPacksQuery,
    ItemDetail_DemoVariantPacksQueryVariables
  >(ITEM_DETAILS_DEMO_VARIANT_PACKS_QUERY, {
    context: {
      anonymous: isGuestMode,
    },
    skip: !restaurantUUID || !supplierUUID || !variantPackUUIDs.length,
    variables: {
      ...itemQueryVariables,
      restaurantUUID: restaurantUUID!,
      supplierUUID: supplierUUID!,
      variantPackUUIDs,
    },
  });

  return useMemo(() => {
    const variantPackMap = (data?.visible_variant_packs || []).reduce<{
      [
        uuid: string
      ]: ItemDetail_DemoVariantPacksQuery['visible_variant_packs'][0]['variant_pack'];
    }>((result, vvp) => {
      const variantPack = vvp.variant_pack;
      if (variantPack) {
        result[variantPack.uuid] = variantPack;
      }

      return result;
    }, {});

    const recommendedVariantPacks = recommendedVariantPackUUIDs
      .map((variantPackUUID) => variantPackMap[variantPackUUID])
      .filter(Boolean) as ItemsContext_VariantsPackFieldsFragment[];
    const relatedVariantPacks = relatedVariantPackUUIDs
      .map((variantPackUUID) => variantPackMap[variantPackUUID])
      .filter(Boolean) as ItemsContext_VariantsPackFieldsFragment[];

    return {
      recommendedVariantPacks: adaptAndGroupVariantPacks(
        recommendedVariantPacks,
      ),
      relatedVariantPacks: adaptAndGroupVariantPacks(relatedVariantPacks),
    };
  }, [
    adaptAndGroupVariantPacks,
    data,
    recommendedVariantPackUUIDs,
    relatedVariantPackUUIDs,
  ]);
};
